/**
 * Returns a quantity formatter object that can be used to format quantity
 * values.
 *
 * @returns A quantity formatter object.
 */
function getQuantityFormatter() {
    return new Intl.NumberFormat(getLocale());
}
/**
 * Returns a currency formatter object that can be used to format currency
 * values.
 *
 * @returns A currency formatter object.
 */
function getCurrancyFormatter() {
    return new Intl.NumberFormat(getLocale(), {
        style: "currency",
        currency: "USD",
        currencyDisplay: "symbol",
    });
}
/**
 * Returns localized day names of the week based on the specified format.
 *
 * @param format The format of the day names to return.
 * @returns An array of localized day names.
 */
export function getDayNames(format) {
    const formatter = new Intl.DateTimeFormat(getLocale(), {
        weekday: format,
        timeZone: "UTC",
    });
    const days = [1, 2, 3, 4, 5, 6, 7].map((day) => {
        const dd = `0${day}`;
        return new Date(`2017-01-${dd}T00:00:00+00:00`);
    });
    return days.map((date) => formatter.format(date));
}
/**
 * Provides a way to obtain an array of localized month names based on the
 * specified format.
 *
 * @param format The format of the month names to return.
 * @returns An array of localized month names.
 */
export function getMonthNames(format = "long") {
    const formatter = new Intl.DateTimeFormat(getLocale(), {
        month: format,
        timeZone: "UTC",
    });
    const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
        const mm = month < 10 ? `0${month}` : month;
        return new Date(`2017-${mm}-01T00:00:00+00:00`);
    });
    return months.map((date) => formatter.format(date));
}
/**
 * Provides a way to obtain a localized string representation of a date,
 * including the month, day, and year, based on the specified format and
 * locale.
 *
 * @param date The date to be formatted.
 * @param forceLocale The locale to use for formatting the date. Defaults to the
 *   HTM language attribute.
 * @returns A localized string representation of the date.
 */
export function getLocalizedDate(date, forceLocale) {
    const formatter = new Intl.DateTimeFormat(forceLocale || getLocale(), {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
    return formatter.format(new Date(date));
}
/**
 * Provides a way to retrieve the locale from the HTML document or fallback to a
 * default locale if it is unable to determine the locale. It is useful for
 * obtaining the language and region settings of the user's environment.
 *
 * @returns The locale of the HTML document or a default locale.
 */
export function getLocale() {
    if (typeof document === "undefined") {
        return "en-US";
    }
    return (document.getElementsByTagName("html")?.[0]?.getAttribute("lang") || "en-US");
}
/**
 * In summary, the getLocalizedQuantity function provides a way to obtain a
 * localized string representation of a quantity based on the specified value,
 * utilizing a separate quantityFormatter object for the formatting process.
 *
 * @param value The quantity value to be formatted.
 * @returns A localized string representation of the quantity value.
 */
export function getLocalizedQuantity(value) {
    return getQuantityFormatter().format(value);
}
/**
 * Provides a way to obtain a localized string representation of a currency
 * value based on the specified value, utilizing a separate currencyFormatter
 * object for the formatting process. It removes the currency code substring and
 * trims any surrounding whitespace from the resulting string.
 *
 * @param value The currency value to be formatted.
 * @returns A localized string representation of the currency value.
 */
export function getLocalizedCurrency(value) {
    return getCurrancyFormatter().format(value).replace("US", "").trim();
}
/**
 * Provides a way to obtain a localized string representation of a month based
 * on the specified date and locale.
 *
 * @param date The date to be formatted.
 * @param forceLocale The locale to use for formatting the month. Defaults to
 *   the HTML language attribute.
 * @returns A localized string representation of the month.
 */
export function getLocalizedMonth(date, forceLocale) {
    const formatter = new Intl.DateTimeFormat(forceLocale || getLocale(), {
        month: "long",
        timeZone: "UTC",
    });
    return formatter.format(new Date(date));
}
/**
 * Provides a way to obtain a localized string representation of a day based on
 * the specified date and locale.
 *
 * @param date The date to be formatted.
 * @param forceLocale The locale to use for formatting the day. Defaults to the
 *   HTML language attribute.
 * @returns A localized string representation of the day.
 */
export function getLocalizedDay(date, forceLocale) {
    const formatter = new Intl.DateTimeFormat(forceLocale || getLocale(), {
        weekday: "long",
        timeZone: "UTC",
    });
    return formatter.format(new Date(date));
}
const suffixes_en = {
    zero: "",
    one: "st",
    two: "nd",
    few: "rd",
    many: "th",
    other: "th",
};
const suffixes_fr = {
    zero: "",
    one: "er",
    two: "e",
    few: "e",
    many: "e",
    other: "e",
};
/**
 * Provides a way to obtain a localized string representation of an ordinal.
 *
 * @param date The date to be formatted.
 * @param forceLocale The locale to use for formatting the ordinal. Defaults to
 *   the HTML language attribute.
 * @returns A localized string representation of the ordinal.
 */
export function getLocalizedOrdinal(date, forceLocale) {
    const locale = forceLocale || getLocale();
    const ordinalFormatter = new Intl.PluralRules(locale, {
        type: "ordinal",
    });
    const dateFormatter = new Intl.DateTimeFormat(forceLocale || getLocale(), {
        day: "numeric",
        timeZone: "UTC",
    });
    const number = parseInt(dateFormatter.format(new Date(date)));
    const category = ordinalFormatter.select(number);
    const suffix = locale === "fr-CA" ? suffixes_fr[category] : suffixes_en[category];
    return number + suffix;
}
//# sourceMappingURL=localization.js.map